import { computed, ref, onMounted, watch } from 'vue';

import { NotificationFilterFormData } from './types';

import {
  parseDate,
  parseNumber,
  parseString,
  NotificationStatus,
  NotificationPeriodicity,
} from '@/shared/lib';

export interface UseNotificationFilterModalOptions {
  defaultCustomPeriodicity: number | null;
  currentFilters?: Partial<NotificationFilterFormData>;
  onFilterUpdate: (filters: Partial<NotificationFilterFormData>) => void;
  onClearFilters: () => void;
}

export function useNotificationFilterModal(options: UseNotificationFilterModalOptions) {
  const { defaultCustomPeriodicity, currentFilters, onFilterUpdate, onClearFilters } = options;

  const formData = ref<NotificationFilterFormData>({
    periodicity: null,
    status: null,
    startDate: null,
    endDate: null,
    customPeriodicity: null,
    customPeriodicityUnit: 'days',
    createdBy: null,
  });

  const isStartDateOpen = ref(false);
  const isEndDateOpen = ref(false);

  const hasInvalidDateRange = computed(() => {
    if (!formData.value.startDate || !formData.value.endDate) {
      return false;
    }

    return formData.value.endDate < formData.value.startDate;
  });

  function loadFilters(filters?: Partial<NotificationFilterFormData>) {
    if (!filters) return;

    const hasFilters = Object.entries(filters).some(
      ([, value]) =>
        value !== null &&
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
    );

    formData.value.periodicity = filters.periodicity ?? null;
    formData.value.status = filters.status ?? null;
    formData.value.startDate = parseDate(filters.startDate);
    formData.value.endDate = parseDate(filters.endDate);
    if ('customPeriodicity' in filters) {
      formData.value.customPeriodicity =
        filters.customPeriodicity === null ? null : parseNumber(filters.customPeriodicity);
    } else if (hasFilters) {
      formData.value.customPeriodicity = defaultCustomPeriodicity;
    }
    formData.value.customPeriodicityUnit = filters.customPeriodicityUnit
      ? parseString(filters.customPeriodicityUnit)
      : 'days';
    formData.value.createdBy = filters.createdBy === null ? null : parseNumber(filters.createdBy);
  }

  onMounted(() => {
    loadFilters(currentFilters);
  });

  watch(
    () => currentFilters,
    (newFilters) => {
      if (newFilters) {
        loadFilters(newFilters);
      }
    },
    { deep: true }
  );

  watch(
    () => formData.value.startDate,
    (startDate) => {
      if (!startDate || !formData.value.endDate) {
        return;
      }

      if (formData.value.endDate < startDate) {
        formData.value.endDate = null;
      }
    }
  );

  const clearFilters = () => {
    Object.assign(formData.value, {
      periodicity: null,
      status: null,
      startDate: null,
      endDate: null,
      customPeriodicity: defaultCustomPeriodicity,
      customPeriodicityUnit: 'days',
      createdBy: null,
    });
  };

  const handleClearFilters = () => {
    clearFilters();
    onClearFilters();
  };

  const applyFilters = () => {
    if (hasInvalidDateRange.value) {
      return;
    }

    onFilterUpdate({
      periodicity: formData.value.periodicity,
      status: formData.value.status,
      startDate: formData.value.startDate,
      endDate: formData.value.endDate,
      customPeriodicity:
        formData.value.periodicity === NotificationPeriodicity.CUSTOM
          ? formData.value.customPeriodicity
          : null,
      customPeriodicityUnit:
        formData.value.periodicity === NotificationPeriodicity.CUSTOM
          ? formData.value.customPeriodicityUnit
          : undefined,
      createdBy: formData.value.createdBy,
    });
  };

  const selectPeriodicity = (periodicity: NotificationPeriodicity) => {
    formData.value.periodicity = formData.value.periodicity === periodicity ? null : periodicity;
  };

  const selectStatus = (status: NotificationStatus) => {
    formData.value.status = formData.value.status === status ? null : status;
  };

  return {
    formData,
    isStartDateOpen,
    isEndDateOpen,
    hasInvalidDateRange,
    loadFilters,
    clearFilters,
    handleClearFilters,
    applyFilters,
    selectPeriodicity,
    selectStatus,
  };
}
